import { useEffect } from "react";
import { postMqttToday } from "@client/api/endpoints";
import { isStatsFresh, useStatsStore } from "@client/store/stats";
import { useUserStore } from "@client/store/user";

type UseTodayProps = {
  topicId: string;
};

/**
 * TTL-cached fetch pro `/api/mqtt/today` (autoritativní StatWindow agregát pro dnes).
 * Refactor 2026-05 (CHART-DATA-REFACTOR Fáze 5): původně počítal stats v JS přes in-memory
 * `valuesStore.history` — to bylo nedostupné po čerstvém loginu (žádná history) a
 * neodpovídalo serverové StatWindow autoritě. Nyní volá REST endpoint s 60s TTL.
 *
 * Loading state se neřeší (komponenta zobrazí "—" do prvního successu). Pokud
 * bude potřeba spinner, derive z `useStatsStore.cache.get(...)` undefined check.
 */
export const useToday = ({ topicId }: UseTodayProps) => {
  const user = useUserStore((s) => s.user);
  const cached = useStatsStore((s) => s.cache.get(`today|${topicId}`));

  useEffect(() => {
    if (!user || !topicId) return;
    const existing = useStatsStore.getState().get(`today|${topicId}`);
    if (existing?.kind === "today" && isStatsFresh(existing, Date.now())) return;

    let aborted = false;
    postMqttToday({ topicId })
      .then((res) => {
        if (aborted) return;
        useStatsStore.getState().put(`today|${topicId}`, {
          kind: "today",
          // Server vrací 200 OK s null pro "dnes ještě žádná data" (legitní stav).
          // Pokud success=false (shouldn't happen — server vždy success), cache null.
          data: res.success ? res.responseObject : null,
          fetchedAt: Date.now(),
        });
      })
      .catch(() => {
        if (aborted) return;
        // Server/network error — cache null s krátkým TTL aby spam request flood
        // nevznikl (stejný useToday se mountuje per topic na hlavní stránce).
        useStatsStore.getState().put(`today|${topicId}`, {
          kind: "today",
          data: null,
          fetchedAt: Date.now(),
        });
      });

    return () => {
      aborted = true;
    };
  }, [user, topicId]);

  const stats = cached?.kind === "today" ? (cached.data ?? undefined) : undefined;
  return { stats, loading: !cached };
};
